import type { LoadedContent } from '@orbomb/content';
import { createSimConfig, type SimConfig } from './config';
import { SIMULATION_VERSION } from './constants';
import { restoreState, serializeState } from './serialization';
import { createMatch, step, type CreateMatchOptions } from './simulation';
import type { MatchState, StepResult, TickInputs } from './types';

export * from './config';
export * from './constants';
export * from './detmath';
export * from './events';
export * from './geometry';
export * from './invariants';
export * from './physics';
export * from './replay';
export * from './rng';
export * from './scenario';
export * from './serialization';
export * from './simulation';
export * from './types';
export { contactArmingAllowed, currentRange, previewCross } from './gameplay/bombs';
export { chooseSpawnCell, evaluateSpawnCells } from './gameplay/respawn';
export { enterSuddenDeath, overtimeSettled } from './gameplay/match';

/** Conceptual API from docs/architecture.md section 5.2, bound to one frozen config. */
export interface Simulation {
  readonly version: string;
  readonly config: SimConfig;
  createMatch(options: CreateMatchOptions): MatchState;
  step(state: MatchState, inputs: TickInputs): StepResult;
  serialize(state: MatchState): string;
  restore(snapshot: string): MatchState;
}

export function createSimulation(content: LoadedContent): Simulation {
  const config = createSimConfig(content);
  return {
    version: SIMULATION_VERSION,
    config,
    createMatch: (options) => createMatch(config, options),
    step: (state, inputs) => step(state, config, inputs),
    serialize: serializeState,
    restore: (snapshot) => restoreState(snapshot, config),
  };
}

/** Remaining regulation time in ticks for the clock display (0 during overtime). */
export function regulationRemainingTicks(state: MatchState): number {
  const { phase, countdownEndTick, regulationEndTick } = state.clock;
  if (phase === 'COUNTDOWN') return regulationEndTick - countdownEndTick;
  return Math.max(0, regulationEndTick - state.tick);
}

export function countdownRemainingTicks(state: MatchState): number {
  return Math.max(0, state.clock.countdownEndTick - state.tick);
}
